import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { openOnboarding } from "./OnboardingPopup";
import { LOGO_URL } from "../lib/api";

const navCls = ({ isActive }) =>
  `relative font-mono-label text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
    isActive ? "text-[#C5A059]" : "text-white/70 hover:text-[#C5A059]"
  }`;

export const Header = () => {
  const { count } = useCart();
  const biz = useSettings().business || {};
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const close = () => setOpen(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["/", "Atelier", "nav-home", true],
    ["/shop", "The Library", "nav-shop"],
    ["/#alchemist", "Scent Alchemist", "nav-alchemist"],
    ["#howitworks", "How It Works", "nav-howitworks", false, "action"],
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 backdrop-blur-xl transition-colors duration-500 ${
        scrolled ? "bg-[#0A0A0A]/92 border-b border-white/10" : "bg-[#0A0A0A]/70 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-12 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group" onClick={close}>
          <img src={biz.logo_url || LOGO_URL} alt={biz.brand_name || "HYPPE AROMA"} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = LOGO_URL; }} className="h-12 w-12 sm:h-14 sm:w-14 object-contain" />
          <div className="hidden sm:block leading-none">
            <p className="font-serif-display text-xl tracking-[0.25em] text-[#F4EFE6]">{biz.brand_name || "HYPPE AROMA"}</p>
            <p className="font-mono-label text-[9px] uppercase tracking-[0.3em] text-[#C5A059] mt-1.5">{biz.tagline || "Enhance Your Natural Aura"}</p>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map(([to, label, testid, end, action]) => (
            action
              ? <button key={testid} type="button" data-testid={testid} className={navCls({ isActive: false })} onClick={() => { close(); openOnboarding(); }}>{label}</button>
              : <NavLink key={to} to={to} end={end} className={navCls} data-testid={testid}>{label}</NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <Link to="/cart" data-testid="header-cart-link" className="relative flex items-center gap-2 text-[#F4EFE6] hover:text-[#C5A059] transition-colors p-1.5" onClick={close}>
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            <span className="font-mono-label text-[10px] uppercase tracking-[0.2em] hidden sm:block">Cart</span>
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 bg-[#C5A059] text-[#0A0A0A] text-[10px] font-mono-label w-5 h-5 flex items-center justify-center rounded-full">
                {count}
              </span>
            )}
          </Link>
          <button data-testid="mobile-menu-toggle" className="lg:hidden text-[#F4EFE6] inline-flex items-center justify-center w-11 h-11 -mr-2" onClick={() => setOpen((v) => !v)}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-white/10 bg-[#0A0A0A]">
          <nav className="flex flex-col px-5 py-6 gap-4">
            {[...links.map(([to, label, testid, end, action]) => [to, label, testid, action]), ["/faq", "FAQ", "nav-faq"], ["/policies", "Policies", "nav-policies"]].map(([to, label, testid, action]) => (
              action
                ? <button key={testid} type="button" data-testid={`${testid}-mobile`} className={`${navCls({ isActive: false })} text-left`} onClick={() => { close(); openOnboarding(); }}>{label}</button>
                : <NavLink key={to} to={to} className={navCls} onClick={close}>{label}</NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};
